import { isActiveStatus, useGetMyProfilesQuery } from "@/assignment"

/**
 * The default post-login destination (product.md §2, "Default landing on
 * login"): a respondent who still has an assignment to act on lands on their
 * personal area; everyone else falls through to the workspace dashboard
 * (which TenantRoute resolves to the last-used / Personal Workspace).
 *
 * Enable it only once the User is authenticated and no explicit redirect — a
 * deep-link return stored before login, or an invitation's questionnaire
 * link — already dictates where to go; those take precedence over this
 * default, so the query stays skipped (and unfired) in that case.
 *
 * `isResolved` gates navigation: it is false while the assignments query is
 * still in flight, so the caller waits rather than flashing the wrong route.
 * A failed query resolves to "/dashboard" so a transient error never traps
 * the User away from their workspace.
 */
export default function useDefaultLanding(enabled: boolean): {
  route: "/myprofiles" | "/dashboard"
  isResolved: boolean
} {
  const { data, isSuccess, isError } = useGetMyProfilesQuery(undefined, {
    skip: !enabled,
  })

  if (!enabled) {
    return { route: "/dashboard", isResolved: false }
  }

  const hasActive = !!data?.items.some((row) => isActiveStatus(row.status))

  return {
    route: hasActive ? "/myprofiles" : "/dashboard",
    isResolved: isSuccess || isError,
  }
}
